import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {finalize, first, Observable, switchMap, tap} from 'rxjs';
import {ScheduleVersion, ScheduleVersionEndpoint, ScheduleVersionListData} from '@api/planner';

export interface VersionActionCallbacks<T> {
  onStart?: () => void;
  onSuccess?: (item: T) => void;
  onComplete?: () => void;
}

@Injectable()
export class ScheduleVersionHelper {

  constructor(
    private snackBar: MatSnackBar,
    private endpoint: ScheduleVersionEndpoint,
    private listData: ScheduleVersionListData,
  ) {}

  formatDate(dateString: string): string {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  saveNote(version: ScheduleVersion, noteText: string): Observable<ScheduleVersion> {
    const noteValue = noteText.trim() || null;
    return this.endpoint.update(version.id, { note: noteValue }).pipe(
      tap(() => {
        version.note = noteValue;
        this.snackBar.open('Note saved', 'Close', { duration: 2000 });
      })
    );
  }

  revertWithDialog(
    scheduleId: number,
    version: ScheduleVersion,
    callbacks?: VersionActionCallbacks<ScheduleVersion>
  ): void {
    if (version.isActive) {
      this.snackBar.open('This version is already active', 'Close', {duration: 2000});
      return;
    }

    const confirmed = confirm(
      `Are you sure you want to revert to version v${version.versionNumber}?\n\n` +
      `This will set this version as active and restore its sessions.`
    );

    if (!confirmed) return;

    callbacks?.onStart?.();

    this.endpoint.revert(scheduleId, version.id)
      .pipe(
        first(),
        tap(response => this.snackBar.open(response.message || 'Version reverted successfully', 'Close', {duration: 3000})),
        switchMap(() => this.listData.loadItems().pipe(first())),
        finalize(() => callbacks?.onComplete?.())
      )
      .subscribe({
        next: () => callbacks?.onSuccess?.(version),
        error: (err) => {
          console.error('Failed to revert version', err);
          this.snackBar.open(err.error?.error || 'Failed to revert version', 'Close', {duration: 3000});
        },
      });
  }

  deleteWithDialog(
    version: ScheduleVersion,
    callbacks?: VersionActionCallbacks<ScheduleVersion>
  ): void {
    if (version.isActive) {
      this.snackBar.open('Cannot delete the active version', 'Close', {duration: 3000});
      return;
    }

    const confirmed = confirm(
      `Are you sure you want to delete version v${version.versionNumber}?\n\n` +
      `This will permanently remove:\n` +
      `- The version record\n` +
      `- All sessions for this version\n` +
      `- All constraint files for this version\n` +
      `- The Lambda function (if deployed)\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) return;

    callbacks?.onStart?.();

    this.endpoint.delete(version.id)
      .pipe(
        first(),
        tap(() => this.snackBar.open('Version deleted', 'Close', {duration: 2000})),
        switchMap(() => this.listData.loadItems().pipe(first())),
        finalize(() => callbacks?.onComplete?.())
      )
      .subscribe({
        next: () => callbacks?.onSuccess?.(version),
        error: (err) => {
          console.error('Failed to delete version', err);
          this.snackBar.open(err.error?.error || 'Failed to delete version', 'Close', {duration: 3000});
        },
      });
  }

  bulkDeleteWithDialog(
    scheduleId: number,
    versions: ScheduleVersion[],
    callbacks?: VersionActionCallbacks<ScheduleVersion[]>
  ): void {
    const activeSelected = versions.find(v => v.isActive);
    if (activeSelected) {
      this.snackBar.open('Cannot delete the active version. Deselect it first.', 'Close', {duration: 3000});
      return;
    }

    if (versions.length === 0) {
      this.snackBar.open('No versions selected', 'Close', {duration: 2000});
      return;
    }

    const confirmed = confirm(
      `Are you sure you want to delete ${versions.length} version(s)?\n\n` +
      `This will permanently remove all selected versions, their sessions, constraint files, and Lambda functions.\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) return;

    callbacks?.onStart?.();

    const ids = versions.map(v => v.id);
    this.endpoint.bulkDelete(scheduleId, ids)
      .pipe(
        first(),
        tap(response => {
          const deletedCount = response.deleted?.length || 0;
          const errorCount = response.errors?.length || 0;
          if (errorCount > 0) {
            this.snackBar.open(`Deleted ${deletedCount} versions, ${errorCount} failed`, 'Close', {duration: 3000});
          } else {
            this.snackBar.open(`Deleted ${deletedCount} versions`, 'Close', {duration: 2000});
          }
        }),
        switchMap(() => this.listData.loadItems().pipe(first())),
        finalize(() => callbacks?.onComplete?.())
      )
      .subscribe({
        next: () => callbacks?.onSuccess?.(versions),
        error: (err) => {
          console.error('Failed to delete versions', err);
          this.snackBar.open(err.error?.error || 'Failed to delete versions', 'Close', {duration: 3000});
        },
      });
  }
}
